import React, { type ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Remove from '@assets/img/icons/e-remove.svg?react';
import { Capacitor } from '@capacitor/core';
import cn from 'classnames';

import { useGetUserDecorationsQuery } from '@/api/queries/user';
import { ENV } from '@/common/app/env';
import { AppRoute } from '@/common/app/routes';
import { useAppleAuth } from '@/common/hooks/apple-auth';
import { useLogout } from '@/common/hooks/logout';
import { isAppleSignInAvailable } from '@/common/lib/apple-sign-in';
import { playSound, updateSoundSettings } from '@/common/lib/soundEffect';
import { AppleLogo } from '@/components/Auth/Buttons/AppleLoginButton';
import {
  getDecorationClass,
  getDecorationTitleKey,
  NO_DECORATION_TITLE,
} from '@/components/Leagues/PositionItem/decorations/registry';
import { toggleSettingMenu } from '@/redux/reducers/modalsReducer';
import { useAppDispatch, useAppSelector } from '@/redux/store';

import css from './style.module.scss';

import NotificationBell from '/img/icons/notification-bell.svg';
import languageIcon from '/img/icons/settings/language.svg';
import sounds from '/img/icons/settings/sounds.svg';
import vibrationIcon from '/img/icons/settings/vibration.svg';

type SettingsItemBase = {
  id: string;
  label: string;
  icon?: string | ReactNode;
  condition?: boolean;
  light?: boolean;
};

type SettingsItemLink = SettingsItemBase & {
  type: 'link';
  action: () => void;
  value?: string;
};

type SettingsItemToggle = SettingsItemBase & {
  type: 'toggle';
  action: () => void;
  state: boolean;
};

type SettingsItem = SettingsItemLink | SettingsItemToggle;

const SettingsSection: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const level = useAppSelector((state) => state.user.user?.level || 0);
  const unreadNotificationsCount = useAppSelector(
    (state) => state.user.unreadNotifications,
  );
  const { t } = useTranslation();
  const [language, setLanguage] = useState('English');
  const [vibration, setVibration] = useState(false);
  const [help, setHelp] = useState(false);
  const [soundEffect, setSoundEffect] = useState(false);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isTelegramMiniApp = !!Telegram?.WebApp?.initData;
  const isAppleLinked = useAppSelector(
    (state) => !!state.user.user?.appleLinked,
  );
  const appleAuth = useAppleAuth();
  // Скины: пункт нужен только тем, у кого есть между чем переключаться.
  // Одного выигранного скина уже достаточно — вторым вариантом всегда идёт
  // «Стандартная плашка», её в items нет (см. ChangeSkin).
  const { data: decorations } = useGetUserDecorationsQuery();
  const ownedSkinsCount = decorations?.items.length ?? 0;
  const activeSkinId = decorations?.activeId ?? '';

  const closeModal = (): void => {
    dispatch(toggleSettingMenu());
  };

  const languagesList = [
    {
      code: 'en',
      language: 'English',
    },
    {
      code: 'ru',
      language: 'Русский',
    },
  ];

  useEffect(() => {
    const lan = localStorage.getItem('i18nextLng') || 'en';
    const obj = languagesList.find((el) => el.code === lan);
    const activeVibr = JSON.parse(
      localStorage.getItem('setHapticFeedback') || 'true',
    );
    setVibration(activeVibr);

    if (obj) setLanguage(obj.language);

    const activeHelp = JSON.parse(localStorage.getItem('help') || 'true');
    setHelp(activeHelp);
    const activeSound = JSON.parse(
      localStorage.getItem('setSoundEffect') || 'true',
    );
    setSoundEffect(activeSound);
  }, []);

  const disableVibration = (): void => {
    setVibration(!vibration);
    const string = JSON.stringify(!vibration);
    localStorage.setItem('setHapticFeedback', string);
  };
  const disableHelp = (): void => {
    setHelp((prevHelp) => {
      const newHelpState = !prevHelp;
      localStorage.setItem('help', JSON.stringify(newHelpState));
      window.dispatchEvent(new Event('storage'));

      return newHelpState;
    });
  };
  const disableSoundEffect = (): void => {
    const newState = !soundEffect;
    setSoundEffect(newState);
    updateSoundSettings(newState);
  };

  const logout = useLogout();
  const handleLogout = async (): Promise<void> => {
    await logout();
    closeModal();
  };

  const settingsItems: SettingsItem[] = [
    {
      id: 'notifications',
      label: t('Notifications'),
      icon: (
        <div className="size-6 relative">
          <img
            src={NotificationBell}
            alt="Notifications"
            className="size-full"
          />
          {unreadNotificationsCount > 0 && (
            <div className="absolute right-0 top-0 bg-button-green-gradient rounded-full size-2.5 shadow-[0_0.5px_0_0_rgba(255,255,255,0.50)_inset]" />
          )}
        </div>
      ),
      type: 'link',
      value:
        unreadNotificationsCount > 0
          ? unreadNotificationsCount.toString()
          : undefined,
      action: (): void => {
        navigate('/notifications');
        closeModal();
      },
      condition: true,
      light: unreadNotificationsCount > 0,
    },
    {
      id: 'language',
      label: t('Language'),
      icon: languageIcon,
      type: 'link',
      action: (): void => {
        navigate('/settings/changeLanguage');
        closeModal();
      },
      value: language,
      // В нативе язык меняется в системных настройках iOS.
      condition: !Capacitor.isNativePlatform(),
    },
    {
      id: 'push',
      label: t('Push notifications'),
      icon: NotificationBell,
      type: 'link',
      action: (): void => {
        navigate(`${AppRoute.SETTINGS}/pushSettings`);
        closeModal();
      },
      condition: Capacitor.isNativePlatform(),
    },
    {
      id: 'skin',
      label: t('Profile skin'),
      // Иконка — сам активный скин: отдельного ассета не нужно, и сразу
      // видно, что надето.
      icon: (
        <div
          className={cn(
            'size-6 rounded-md relative overflow-hidden',
            getDecorationClass(activeSkinId) ?? 'bg-white/10',
          )}
        />
      ),
      type: 'link',
      action: (): void => {
        navigate(`${AppRoute.SETTINGS}/changeSkin`);
        closeModal();
      },
      value: t(
        activeSkinId
          ? getDecorationTitleKey(activeSkinId)
          : NO_DECORATION_TITLE,
      ),
      condition: ownedSkinsCount > 0,
    },
    {
      id: 'vibration',
      label: t('Vibration'),
      icon: vibrationIcon,
      type: 'toggle',
      condition: true,
      state: vibration,
      action: disableVibration,
    },
    {
      id: 'tips',
      label: t('Tips'),
      icon: '/img/icons/helpCards/helpIcon.webp',
      type: 'toggle',
      condition: level < ENV.MIN_LEVEL_FOR_HELPICON,
      state: help,
      action: disableHelp,
    },
    {
      id: 'sounds',
      label: t('Sounds'),
      icon: sounds,
      type: 'toggle',
      condition: true,
      state: soundEffect,
      action: disableSoundEffect,
    },
    {
      id: 'terms',
      label: t('Terms of Use'),
      type: 'link',
      action: (): void => {
        navigate(AppRoute.TERMS);
        closeModal();
      },
      condition: true,
    },
    {
      id: 'privacy',
      label: t('Privacy Policy'),
      type: 'link',
      action: (): void => {
        navigate(AppRoute.PRIVACY);
        closeModal();
      },
      condition: true,
    },
    {
      id: 'linkApple',
      label: isAppleLinked ? t('Apple ID') : t('Link Apple ID'),
      icon: <AppleLogo className="size-6 text-white" />,
      type: 'link',
      value: appleAuth.isLoading
        ? '…'
        : isAppleLinked
          ? t('Linked')
          : undefined,
      action: (): void => {
        if (!isAppleLinked) void appleAuth.link();
      },
      condition: isAppleSignInAvailable(),
    },
    {
      id: 'logout',
      label: t('Log out'),
      type: 'link',
      action: handleLogout,
      condition: !isTelegramMiniApp,
    },
  ];

  return (
    <div className={css.settings}>
      <button
        className="p-1 rounded-full bg-white/10 backdrop-blur-lg"
        onClick={onClose}
      >
        <Remove className="size-6" />
      </button>
      <h3 className={css.settingsTitle}>{t('Settings')}</h3>
      <ul className={css.settingsList}>
        {settingsItems.map((item) => {
          if (!item.condition) {
            return null;
          }

          return (
            <li
              key={item.id}
              className={css.settingsListElement}
              onClick={() => {
                playSound(
                  `buttons/${item.type === 'link' ? 'menu' : 'switch'}`,
                );
                item.action();
              }}
            >
              <div>
                {typeof item.icon === 'string' ? (
                  item.icon.endsWith('.webp') ? (
                    <picture>
                      <source srcSet={item.icon} type="image/webp" />
                      <img
                        src={item.icon.replace('.webp', '.png')}
                        alt={item.label}
                        width={24}
                      />
                    </picture>
                  ) : (
                    <img
                      src={item.icon}
                      alt={item.label}
                      className="w-[24px] h-[24px]"
                    />
                  )
                ) : (
                  item.icon
                )}
                <span>{item.label}</span>
              </div>

              {item.type === 'link' && (
                <div>
                  {item.value && (
                    <span
                      className={cn(
                        item.id === 'notifications' && 'text-[#82E024]',
                      )}
                    >
                      {item.value}
                    </span>
                  )}
                  <img src="/img/icons/arrow-go.svg" alt="" />
                </div>
              )}

              {item.type === 'toggle' && (
                <div className={cn(css.sliderChange, item.state && css.active)}>
                  <div onClick={item.action}></div>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default SettingsSection;
